import { isDefined } from "@clipboard-health/util-ts";
import { Paper, Stack, useTheme } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import {
  NumbersBreakdown,
  ReviewsSummary,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { useCallback, useEffect, useRef } from "react";

import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { WorkplaceHighlightPills } from "../Highlights/Pills";
import { useWorkplaceHighlights } from "../Highlights/useWorkplaceHighlights";
import { WorkplaceCommentList } from "./Comment/List";
import { WorkplaceReviewsFooter } from "./Footer";
import { PatientLoadSection } from "./PatientLoadSection";
import { RatingSummary } from "./RatingSummary";
import { SubCategoryRatings } from "./SubCategoryRatings";
import { useDelayedWorkplaceReviewFlag } from "./useDelayedWorkplaceReviewFlag";

export interface WorkplaceReviewsModalProps {
  modalState: UseModalState;
  workplaceId: string;
  workplaceType?: string;
}

export function WorkplaceReviewsModal(props: WorkplaceReviewsModalProps) {
  const { modalState, workplaceId, workplaceType } = props;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const showNewRatingSummary = useDelayedReviewUI || isReviewUXUpdateEnabled;
  const showGeneralRating = !isReviewUXUpdateEnabled;
  const showSubCategoryRatings = isReviewUXUpdateEnabled;
  const showPatientLoad = useDelayedReviewUI || isReviewUXUpdateEnabled;
  const showHighlights = !isReviewUXUpdateEnabled;

  const { data: ratingBreakdowns, isSuccess: ratingBreakdownsIsSuccess } = useGetRatingAggregates(
    String(workplaceId)
  );

  const { highlights, isLoading: isHighlightsLoading } = useWorkplaceHighlights({
    workplaceId,
    includePatientRatioHighlights: !useDelayedReviewUI,
    enabled: showHighlights,
  });

  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(scrollRef);

  const scrollContainerId = `workplace-reviews-scroll-container-${workplaceId}`;
  const scrollPositionKey = `workplace-reviews-scroll-position-${workplaceId}`;

  const saveScrollPosition = useCallback(() => {
    const container = document.querySelector(`#${scrollContainerId}`);
    if (isDefined(container)) {
      sessionStorage.setItem(scrollPositionKey, container.scrollTop.toString());
    }
  }, [scrollContainerId, scrollPositionKey]);

  // Restore scroll position on mount. A timeout derived from the theme's
  // leavingScreen duration (+ buffer) is used because ModalTransitionSwitch
  // briefly renders two instances of this component during the exit transition.
  // The delay ensures the exiting instance has unmounted (and cleared its own
  // timeout) before we restore and clean up.
  useEffect(() => {
    const savedPosition = sessionStorage.getItem(scrollPositionKey);
    if (!isDefined(savedPosition)) {
      return undefined;
    }

    const position = Number.parseInt(savedPosition, 10);
    if (position === 0) {
      sessionStorage.removeItem(scrollPositionKey);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      scrollRef.current?.scrollTo(0, position);
      sessionStorage.removeItem(scrollPositionKey);
    }, theme.transitions.duration.leavingScreen + 50); // Buffer to ensure the exiting instance has unmounted

    return () => {
      clearTimeout(timeoutId);
    };
  }, [scrollPositionKey, theme.transitions.duration.leavingScreen]);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <StickyScrollPageHeader
        headerVariant="tertiary"
        title="Workplace Reviews"
        scrollContainerRef={scrollRef}
        isLoading={false}
      />

      <Stack ref={scrollRef} id={scrollContainerId} sx={{ overflow: "auto", height: "100%" }}>
        {ratingBreakdownsIsSuccess && (
          <Paper variant="tertiary" sx={{ px: 7, paddingBottom: 10 }}>
            <Stack spacing={8}>
              {showNewRatingSummary ? (
                <RatingSummary
                  rating={ratingBreakdowns.avgRating}
                  ratingsCount={ratingBreakdowns.numberOfRatings}
                />
              ) : (
                <ReviewsSummary
                  rating={ratingBreakdowns.avgRating}
                  ratingsCount={ratingBreakdowns.numberOfRatings}
                />
              )}

              {showGeneralRating && ratingBreakdowns.numberOfRatings > 0 && (
                <>
                  <Text semibold variant="h6" sx={{ paddingTop: 3 }}>
                    General Rating
                  </Text>

                  <NumbersBreakdown
                    numberBreakdowns={ratingBreakdowns.ratingsBreakdown.map((rating) => ({
                      value: rating.rating,
                      count: rating.numberOfRatings,
                    }))}
                    defaultValues={[1, 2, 3, 4, 5]}
                  />
                </>
              )}

              {showSubCategoryRatings && <SubCategoryRatings workplaceId={workplaceId} />}

              {showPatientLoad && <PatientLoadSection workplaceId={workplaceId} />}

              {showHighlights && highlights.length > 0 && (
                <>
                  <Text semibold variant="h6" sx={{ paddingTop: 3 }}>
                    Highlights
                  </Text>

                  <WorkplaceHighlightPills
                    highlights={highlights}
                    isLoading={isHighlightsLoading}
                  />
                </>
              )}
            </Stack>
          </Paper>
        )}

        <Paper variant="secondary" sx={{ px: 5, py: 10, flexGrow: 1 }}>
          <WorkplaceCommentList
            workplaceId={workplaceId}
            onBeforeNavigateToComment={saveScrollPosition}
          />
        </Paper>
      </Stack>

      <WorkplaceReviewsFooter workplaceId={workplaceId} workplaceType={workplaceType} />
    </FullScreenDialog>
  );
}
